/**
 * Acompanhamento público da denúncia pelo protocolo — sem login.
 *
 * O relator entra com o protocolo que recebeu e vê o status, as mensagens do comitê e pode
 * complementar. Nada aqui identifica quem denunciou.
 */

import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import denunciaService from '../../services/denunciaService';
import {
  AcompanhamentoDoRelator,
  COR_DO_STATUS,
  ROTULO_STATUS,
  ROTULO_CATEGORIA,
} from '../../types/denuncia';

export const DenunciaStatusPage: React.FC = () => {
  const [protocolo, setProtocolo] = useState('');
  const [dados, setDados] = useState<AcompanhamentoDoRelator | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [mensagem, setMensagem] = useState('');

  const buscar = useCallback(async () => {
    setErro(null);
    setBuscando(true);
    try {
      setDados(await denunciaService.acompanhar(protocolo.trim()));
    } catch (err: unknown) {
      setDados(null);
      setErro(err instanceof Error ? err.message : 'Protocolo não encontrado.');
    } finally {
      setBuscando(false);
    }
  }, [protocolo]);

  const enviarMensagem = useCallback(async () => {
    if (!mensagem.trim() || !dados) return;
    await denunciaService.enviarMensagem(dados.protocolo, mensagem.trim());
    setMensagem('');
    setDados(await denunciaService.acompanhar(dados.protocolo));
  }, [mensagem, dados]);

  const [anexado, setAnexado] = useState<string[]>([]);
  const anexarProva = useCallback(async (arquivo: File | undefined) => {
    if (!arquivo || !dados) return;
    await denunciaService.anexarPorProtocolo(dados.protocolo, arquivo);
    setAnexado((a) => [...a, arquivo.name]);
  }, [dados]);

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>Acompanhar denúncia</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Informe o protocolo que você recebeu ao registrar a denúncia.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField label="Protocolo" value={protocolo} fullWidth
            onChange={(e) => setProtocolo(e.target.value)} />
          <Button variant="contained" onClick={() => void buscar()} disabled={buscando || !protocolo.trim()}>
            Buscar
          </Button>
        </Stack>

        {erro && <Alert severity="error">{erro}</Alert>}

        {dados && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip color={COR_DO_STATUS[dados.status]} label={ROTULO_STATUS[dados.status]} />
              <Typography color="text.secondary">{ROTULO_CATEGORIA[dados.categoria]}</Typography>
            </Stack>
            <Typography variant="h6">{dados.titulo}</Typography>
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>Andamento</Typography>
            {dados.mensagens.length === 0 ? (
              <Typography color="text.secondary">Ainda não há mensagens do comitê.</Typography>
            ) : (
              <Stack spacing={1}>
                {dados.mensagens.map((m, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {m.origem === 'RELATOR' ? 'Você' : 'Comitê'} · {new Date(m.em).toLocaleString('pt-BR')}
                    </Typography>
                    <Typography>{m.texto}</Typography>
                  </Paper>
                ))}
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />
            <TextField label="Complementar (opcional)" multiline minRows={2} fullWidth
              value={mensagem} onChange={(e) => setMensagem(e.target.value)} sx={{ mb: 1 }} />
            <Stack direction="row" spacing={1} alignItems="center">
              <Button onClick={() => void enviarMensagem()} disabled={!mensagem.trim()}>Enviar mensagem</Button>
              <Button variant="outlined" component="label">
                Anexar prova
                <input hidden type="file" accept=".pdf,image/*"
                  onChange={(e) => void anexarProva(e.target.files?.[0])} />
              </Button>
            </Stack>
            {anexado.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {anexado.map((nome, i) => <Typography key={i} variant="body2" color="success.main">✓ {nome}</Typography>)}
              </Stack>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DenunciaStatusPage;
